import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put } from '@/lib/api';
import { fmtDateTimeIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Code2, Mail, Save, ExternalLink, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDerivedState } from '@/hooks/use-derived-state';
import EmailHtmlEditor from '@/components/email/EmailHtmlEditor';
import { fromEditorHtml } from '@/lib/emailTemplateJinjaDom';
import { sanitizeEmailHtml } from '@/lib/sanitizeEmailHtml';

const FOOTER_INSERT_VARS = ['org_name'];

function sanitizeFooterHtml(html) {
  return sanitizeEmailHtml(fromEditorHtml(html || ''));
}

function footersDraftFromSetting(setting) {
  if (!setting) return null;
  return {
    footer_a_html: setting.footer_a_html || '',
    footer_a_text: setting.footer_a_text || '',
    footer_b_html: setting.footer_b_html || '',
    footer_b_text: setting.footer_b_text || '',
  };
}

export default function GlobalSettings() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Global Settings"
        subtitle="Platform-wide defaults for organization branding, email footers, external integrations, and code-editor URL."
      />

      <OrgNameSettingCard />
      <EmailFootersSettingCard />

      <Card className="card-elevated" data-testid="platform-defaults-placeholder">
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold">Platform defaults</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Link-expiry presets, Candidate ID format, data retention, and notification defaults will be
            configurable here soon. For now, these use safe default settings.
          </p>
        </CardContent>
      </Card>

      <CodeEditorSettingCard />
    </div>
  );
}

function OrgNameSettingCard() {
  const qc = useQueryClient();
  const { data: setting } = useQuery({
    queryKey: ['v1-org-name-setting'],
    queryFn: () => v1Get('/settings/org-name'),
  });
  const [draft, setDraft] = useDerivedState(setting?.org_name || '');

  if (!setting) return null;
  const current = setting.org_name || '';
  const dirty = (draft || '') !== current;
  const trimmed = (draft || '').trim();

  const save = async () => {
    if (!trimmed) {
      toast.error('Organization name is required');
      return;
    }
    try {
      await v1Put('/settings/org-name', { org_name: trimmed });
      qc.invalidateQueries({ queryKey: ['v1-org-name-setting'] });
      toast.success('Organization name saved');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (typeof d === 'string' ? d : 'Save failed');
      toast.error(msg);
    }
  };

  return (
    <Card className="card-elevated" data-testid="org-name-setting-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">Organization name</p>
        </div>
        <p className="text-xs text-muted-foreground">
          Used in all email templates as {'{{ org_name }}'}. Change it here to update branding across
          transactional emails without editing each template.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground">Organization name</label>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Kadel Labs"
              maxLength={160}
              data-testid="org-name-input"
            />
          </div>
          <Button onClick={save} disabled={!dirty || !trimmed} className="gap-1.5" data-testid="org-name-save">
            <Save className="h-4 w-4" /> Save
          </Button>
        </div>
        {setting.updated_at && (
          <p className="text-[11px] text-muted-foreground">Last updated {fmtDateTimeIST(setting.updated_at)}</p>
        )}
      </CardContent>
    </Card>
  );
}

function EmailFootersSettingCard() {
  const qc = useQueryClient();
  const { data: setting } = useQuery({
    queryKey: ['v1-email-footers-setting'],
    queryFn: () => v1Get('/settings/email-footers'),
  });
  // Do not pass a fresh object into useDerivedState — reference inequality loops forever.
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft(footersDraftFromSetting(setting));
  }, [setting]);

  if (!setting || !draft) return null;

  const dirty =
    draft.footer_a_html !== (setting.footer_a_html || '')
    || draft.footer_a_text !== (setting.footer_a_text || '')
    || draft.footer_b_html !== (setting.footer_b_html || '')
    || draft.footer_b_text !== (setting.footer_b_text || '');

  const save = async () => {
    const footer_a_html = sanitizeFooterHtml(draft.footer_a_html);
    const footer_b_html = sanitizeFooterHtml(draft.footer_b_html);
    if (!footer_a_html.trim() || !footer_b_html.trim()) {
      toast.error('Footer A and Footer B HTML are required');
      return;
    }
    try {
      await v1Put('/settings/email-footers', {
        footer_a_html,
        footer_a_text: (draft.footer_a_text || '').trim(),
        footer_b_html,
        footer_b_text: (draft.footer_b_text || '').trim(),
      });
      qc.invalidateQueries({ queryKey: ['v1-email-footers-setting'] });
      toast.success('Email footers saved');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (typeof d === 'string' ? d : 'Save failed');
      toast.error(msg);
    }
  };

  return (
    <Card className="card-elevated" data-testid="email-footers-setting-card">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold">Email footers</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Shared footers for transactional emails. Use {'{{ footer_a | safe }}'} or{' '}
              {'{{ footer_b | safe }}'} in a template (same editor as Email Templates). Edit here once
              to update every template that references that footer.
            </p>
          </div>
          <Button onClick={save} disabled={!dirty} className="gap-1.5 shrink-0" data-testid="email-footers-save">
            <Save className="h-4 w-4" /> Save
          </Button>
        </div>

        <div className="space-y-3" data-testid="email-footer-a-editor">
          <p className="text-xs font-semibold text-foreground">Footer A</p>
          <p className="text-[11px] text-muted-foreground">
            Marketing / interview footer. Type # to insert {'{{ org_name }}'}.
          </p>
          <EmailHtmlEditor
            key={`footer-a-${setting.updated_at || 'default'}`}
            value={draft.footer_a_html}
            onChange={(html) => setDraft({ ...draft, footer_a_html: html })}
            variables={FOOTER_INSERT_VARS}
            data-testid="email-footer-a-html-editor"
          />
          <div className="hidden">
            <Textarea
              rows={4}
              value={draft.footer_a_text}
              onChange={(e) => setDraft({ ...draft, footer_a_text: e.target.value })}
              className="font-mono text-[13px]"
              data-testid="email-footer-a-text-input"
            />
          </div>
        </div>

        <div className="space-y-3" data-testid="email-footer-b-editor">
          <p className="text-xs font-semibold text-foreground">Footer B</p>
          <p className="text-[11px] text-muted-foreground">
            Short sign-off used by invites, OTC, password, and similar templates.
          </p>
          <EmailHtmlEditor
            key={`footer-b-${setting.updated_at || 'default'}`}
            value={draft.footer_b_html}
            onChange={(html) => setDraft({ ...draft, footer_b_html: html })}
            variables={FOOTER_INSERT_VARS}
            data-testid="email-footer-b-html-editor"
          />
          <div className="hidden">
            <Textarea
              rows={3}
              value={draft.footer_b_text}
              onChange={(e) => setDraft({ ...draft, footer_b_text: e.target.value })}
              className="font-mono text-[13px]"
              data-testid="email-footer-b-text-input"
            />
          </div>
        </div>

        {setting.updated_at && (
          <p className="text-[11px] text-muted-foreground">Last updated {fmtDateTimeIST(setting.updated_at)}</p>
        )}
      </CardContent>
    </Card>
  );
}

function CodeEditorSettingCard() {
  const qc = useQueryClient();
  const { data: setting } = useQuery({
    queryKey: ['v1-code-editor-setting'],
    queryFn: () => v1Get('/settings/code-editor'),
  });
  const [draft, setDraft] = useDerivedState(setting?.url || '');

  if (!setting) return null;
  const currentUrl = setting.url || '';
  const dirty = (draft || '') !== currentUrl;
  const trimmed = (draft || '').trim();

  const save = async () => {
    try {
      await v1Put('/settings/code-editor', { url: trimmed || null });
      qc.invalidateQueries({ queryKey: ['v1-code-editor-setting'] });
      toast.success('Code-Editor URL saved');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (typeof d === 'string' ? d : 'Save failed');
      toast.error(msg);
    }
  };

  return (
    <Card className="card-elevated" data-testid="code-editor-setting-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">External Code Editor</p>
        </div>
        <p className="text-xs text-muted-foreground">
          When a candidate opens a CODE-stage invitation link, this hosted URL is what they&apos;re sent to.
          This platform does NOT host the editor &mdash; set the URL of your externally-hosted instance here.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground">Hosted editor URL</label>
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="https://your-code-editor.example.com/start" data-testid="code-editor-url-input" />
          </div>
          <Button onClick={save} disabled={!dirty} className="gap-1.5" data-testid="code-editor-url-save"><Save className="h-4 w-4" /> Save</Button>
          {setting.url && (
            <a href={setting.url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5"><ExternalLink className="h-4 w-4" /> Test</Button>
            </a>
          )}
        </div>
        {setting.updated_at && (
          <p className="text-[11px] text-muted-foreground">Last updated {fmtDateTimeIST(setting.updated_at)}</p>
        )}
      </CardContent>
    </Card>
  );
}
